import { useMemo } from "react";
import { parseISO } from "date-fns";
import type { DayPickerBase, Matcher } from "react-day-picker";

import { IssueRangeDayContent } from "./IssueRangeDayContent";
import { RANGE_END, RANGE_MIDDLE, RANGE_START } from "./issueDateRangeModifiers";

/** Which of an issue's two dates a calendar is editing. */
export type IssueDateField = "start" | "due";

/** The slice of calendar props that paints an issue's schedule. Spread onto a
 *  `Calendar` (or a `DatePicker`'s `calendarProps`). */
export type IssueDateRangeCalendarProps = Pick<
  DayPickerBase,
  "defaultMonth" | "modifiers" | "modifiersClassNames" | "components"
>;

// react-day-picker joins the day's base (a ghost button), `day_today`,
// `day_selected` and these classes into one attribute, so which `bg-*`,
// `text-*` or `rounded-*` wins falls to stylesheet order — and the ghost
// button's `hover:bg-accent` sorts after every plain `bg-*`, greying a marked
// day under the pointer. The range marks must win on every day, so they carry `!`.
const ENDPOINT_CLASS = "!bg-primary !text-primary-foreground";

const MIDDLE_CLASS = "!rounded-none !bg-primary/15 !text-foreground hover:!bg-primary/25";

function parseDate(value: string | null | undefined): Date | undefined {
  if (!value) return undefined;
  const date = parseISO(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

const RANGE_COMPONENTS: IssueDateRangeCalendarProps["components"] = {
  DayContent: IssueRangeDayContent,
};

/**
 * Calendar props that show an issue's whole schedule while one of its dates is
 * edited, so the start and due pickers each show the other.
 *
 * - Opens on the edited date's month, else the counterpart's — picking a due
 *   date after a 9 November start lands on November, not today.
 * - Both dates are solid red; the days between them light red. The edited
 *   date's calendar keeps `mode="single"`, so clicking still sets only that
 *   date — the counterpart is shown, never changed.
 * - An inverted pair (start after due) marks both ends but paints no span:
 *   the pair is legal and warned about elsewhere (see utils/issueDates.ts), and
 *   a band running backwards would describe a window that does not exist.
 * - The endpoints carry their role as screen-reader text, so the counterpart
 *   is announced and not only coloured. (react-day-picker 8 never reads
 *   `labels.labelDay`; a day's accessible name is its content.)
 */
export function useIssueDateRangeCalendar(
  field: IssueDateField,
  startDate: string | null | undefined,
  dueDate: string | null | undefined
): IssueDateRangeCalendarProps {
  return useMemo(() => {
    const start = parseDate(startDate);
    const due = parseDate(dueDate);
    const own = field === "start" ? start : due;
    const counterpart = field === "start" ? due : start;
    // A span only exists when there are days strictly between the two ends.
    const spans = start !== undefined && due !== undefined && start < due;

    const modifiers: Record<string, Matcher | Matcher[]> = {
      [RANGE_START]: start ?? [],
      [RANGE_END]: due ?? [],
      // An interval is exclusive at both ends. A `{ from, to }` range of the
      // days inside would invert for adjacent dates, and react-day-picker
      // swaps an inverted range rather than matching nothing.
      [RANGE_MIDDLE]: spans && start && due ? { after: start, before: due } : [],
    };

    // With a span, the endpoints square off on the side facing it so the
    // three pieces read as one bar.
    const modifiersClassNames: Record<string, string> = {
      [RANGE_START]: spans ? `${ENDPOINT_CLASS} !rounded-r-none` : ENDPOINT_CLASS,
      [RANGE_END]: spans ? `${ENDPOINT_CLASS} !rounded-l-none` : ENDPOINT_CLASS,
      [RANGE_MIDDLE]: MIDDLE_CLASS,
    };

    return {
      defaultMonth: own ?? counterpart,
      modifiers,
      modifiersClassNames,
      components: RANGE_COMPONENTS,
    };
  }, [field, startDate, dueDate]);
}
